import { FC, useState } from 'react'
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from 'use-query-params'
import { Spin } from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { useGetTopOrganicPagesList } from 'container/SEOTools/hooks/useGetTopOrganicPagesList'
import { getDistributionColumns, getListColumns } from './columns'
import Table from '../Table'
import StatsWrapper from '../StatsWrapper'
import {
  DateRange,
  Device,
  TopOrganicPagesDistributionApi,
  Tab,
  ListType,
} from '../types'
import { SMALL_PAGE_LIMIT } from '../constants'

type Props = {
  domainId: string
  viewFullPage: boolean
  period: DateRange
  date: string
  distributionResponse: {
    data?: TopOrganicPagesDistributionApi
    isLoading: boolean
  }
  deviceCategory?: Device
  handleViewMore: (list?: ListType) => void
}

export const TopOrganicPages: FC<Props> = ({
  domainId,
  handleViewMore,
  viewFullPage,
  period,
  date,
  deviceCategory,
  distributionResponse,
}) => {
  const [tab, setTab] = useState<Tab>('DISTRIBUTED')
  const [limit, setLimit] = useState(SMALL_PAGE_LIMIT)
  const companyId = useSelectedCompanyId()

  const [insights] = useQueryParam(
    'insights',
    withDefault(StringParam, undefined)
  )
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 1))

  const showInsights = insights === 'true'

  const {
    data: list,
    isLoading: isListLoading,
    error: listError,
  } = useGetTopOrganicPagesList({
    companyId,
    period,
    isEnabled: tab === 'LIST',
    page,
    date,
    domainId,
    limit,
    deviceCategory,
  })

  const handleTabChange = (value: Tab) => {
    setTab(value)
  }

  const handleLimitUpdation = () => {
    viewFullPage ? handleViewMore() : handleViewMore('PAGES')
    !viewFullPage ? setLimit(15) : setLimit(5)
    viewFullPage && setPage(1)
  }

  return (
    <StatsWrapper
      headerText="Top Organic Pages"
      handleTabChange={handleTabChange}
      activeTab={tab}
      handleViewMore={handleLimitUpdation}
      showBack={viewFullPage}
    >
      <Spin
        spinning={
          tab === 'DISTRIBUTED' ? distributionResponse.isLoading : isListLoading
        }
      >
        {tab === 'DISTRIBUTED' ? (
          <Table
            data={distributionResponse?.data?.data?.distribution}
            columns={getDistributionColumns()}
            insights={distributionResponse?.data?.data?.insights}
            showFooter={showInsights}
          />
        ) : (
          <Table
            data={list}
            columns={getListColumns()}
            insights={distributionResponse?.data?.data?.insights}
            showFooter={!viewFullPage && showInsights}
            pagination={
              viewFullPage
                ? {
                    onChange: pageNumber => setPage(pageNumber),
                    hideOnSinglePage: true,
                    position: ['bottomCenter'],
                    pageSize: limit,
                    total: list?.[0]?.total,
                    showSizeChanger: false,
                    current: page,
                  }
                : false
            }
          />
        )}
      </Spin>
    </StatsWrapper>
  )
}
